import { useEffect, useReducer } from "react";
import { useSelector } from "react-redux";
import { msSinceDroneTelemetry } from "../../lib/droneTelemetryClock";
import { missionProgressLabel } from "../../utils/missionProgress";
import { useT } from "../../i18n";

// REST 하트비트가 "끊김"이어도 이 시간 안에 WS 프레임을 받았으면 살아있는 것으로
// 본다 — 폴링(5초) 주기의 낡음이 드론이 막 켜진 순간 오프라인 오표시를 내는 것 방지.
// 가상기/실드론 텔레메트리 주기(2초) 기준 3~4회 유실이어야 만료되는 값.
const WS_FRESH_MS = 8_000;

/**
 * FlightNote
 * ------------------------------------------------------------
 * 지도 좌상단 비행 상태 표시. 신규 UI(Calm) 시안의 상단 알약 배지에 해당한다.
 *
 * 실데이터
 *  - state.drone.control.missionStatus : 미션 수행 여부
 *  - state.drone.missionReturning      : 복귀(RTH) 여부
 *  - state.drone.droneData.waypoint_done / waypoint_total : 실시간 진행 구역 (WebSocket)
 *  - state.drone.infoConnected         : 서버 하트비트 판정 (정보 탭과 동일 소스)
 */
export default function FlightNote() {
  const t = useT();
  const missionStatus = useSelector((state) => state.drone.control?.missionStatus);
  const missionReturning = useSelector((state) => state.drone.missionReturning);
  const missionProgress = useSelector((state) => state.drone.missionProgress);
  const powerStatus = useSelector((state) => state.drone.control?.powerStatus);
  const droneData = useSelector((state) => state.drone.droneData);
  const infoConnected = useSelector((state) => state.drone.infoConnected);
  const farmId = useSelector((state) => state.farm.farmId);
  const [, retick] = useReducer((n) => n + 1, 0);

  const msSince = msSinceDroneTelemetry();
  const wsFresh = msSince < WS_FRESH_MS;

  // 서버는 끊김(false)인데 WS 신선도 창 덕에 살아있음으로 보는 동안에는,
  // 창이 만료되는 시점에 스스로 한 번 재평가한다 — WS 가 멈추면 리렌더
  // 계기가 없어서 배지가 "전원 ON"에 고착되는 것 방지.
  useEffect(() => {
    if (infoConnected !== false || !wsFresh) return undefined;
    const timerId = setTimeout(retick, WS_FRESH_MS - msSince + 100);
    return () => clearTimeout(timerId);
  });

  if (!farmId) return null;

  const done = Number(droneData?.waypoint_done);
  const total = Number(droneData?.waypoint_total);

  // 서버가 "끊김"이라고 확정(false)했고 최근 WS 프레임도 없으면 오프라인.
  // null(판정 없음)은 오프라인으로 강등하지 않는다 — 첫 응답 전 오표시 방지.
  const droneAlive = infoConnected !== false || wsFresh;

  // [mission_progress] 드론이 보고한 수행 단계로 배지 문구를 구체화한다.
  // 비행 단계(이륙/이동/호버링/촬영)는 "비행 중" 자리에, 복귀/착륙은 hold 배지에,
  // 지상 단계(업로드 완료/미션 완료)는 "대기 중" 자리에 반영. 모르는 값은 기존 문구 유지.
  const progressKey =
    typeof missionProgress === "string" ? missionProgress.trim().toLowerCase() : null;
  const FLYING_PHASES = ["takeoff", "moving", "hovering", "capturing"];
  const flyingPhaseLabel = FLYING_PHASES.includes(progressKey)
    ? missionProgressLabel(progressKey)
    : null;

  let text = t("common.flightNote.idle");
  let variant = "idle";

  if (!droneAlive) {
    text = t("common.flightNote.offline");
    variant = "off";
  } else if (progressKey === "landing") {
    text = t("common.flightNote.landing");
    variant = "hold";
  } else if (missionReturning || progressKey === "returning") {
    text = t("common.flightNote.returning");
    variant = "hold";
  } else if (missionStatus) {
    variant = "live";
    const phase = flyingPhaseLabel ?? t("common.flightNote.flying");
    text = Number.isFinite(done) && done > 0
      ? Number.isFinite(total) && total > 0
        ? t("common.flightNote.progressOfTotal", { phase, done, total })
        : t("common.flightNote.progressCount", { phase, done })
      : phase;
  } else if (powerStatus) {
    if (progressKey === "uploaded") {
      text = t("common.flightNote.powerUploaded");
    } else if (progressKey === "complete") {
      text = t("common.flightNote.powerComplete");
    } else {
      text = t("common.flightNote.powerIdle");
    }
  } else {
    text = t("common.flightNote.offline");
    variant = "off";
  }

  return (
    <div className={`calm-note calm-note--${variant}`}>
      <i />
      <span>{text}</span>
    </div>
  );
}
